/**
 * Display units: storage stays metric (km, kg, cm); convert at UI boundaries.
 */
export type UnitSystem = "metric" | "imperial";

const STORAGE_KEY = "straiv_units";
export const UNITS_CHANGED_EVENT = "straiv-units-changed";

export function getStoredUnits(): UnitSystem {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "imperial" || v === "metric") return v;
  } catch {}
  return "metric";
}

/** True when the user (or app) has explicitly written a units preference on this device. */
export function hasStoredUnits(): boolean {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "imperial" || v === "metric";
  } catch {
    return false;
  }
}

export function setStoredUnits(units: UnitSystem): void {
  try {
    localStorage.setItem(STORAGE_KEY, units);
  } catch {}
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(UNITS_CHANGED_EVENT, { detail: units }));
    }
  } catch {}
}

/**
 * Prefer profile.units, then explicit device storage, then locale.
 * Live Settings toggles also write storage + profile; useUnits applies an optimistic override.
 */
export function resolveUnitSystem(profileUnits?: string | null): UnitSystem {
  if (profileUnits === "imperial" || profileUnits === "metric") return profileUnits;
  if (hasStoredUnits()) return getStoredUnits();
  return defaultUnitsFromLocale();
}

export function defaultUnitsFromLocale(): UnitSystem {
  try {
    if (typeof navigator !== "undefined" && /^en-US/i.test(navigator.language)) {
      return "imperial";
    }
  } catch {}
  return "metric";
}

const KM_PER_MILE = 1.609344;
const LB_PER_KG = 2.2046226218;
const IN_PER_CM = 1 / 2.54;

export function metersToDisplayDistance(meters: number, units: UnitSystem): number {
  const km = meters / 1000;
  return units === "imperial" ? km / KM_PER_MILE : km;
}

export function kmToDisplayDistance(km: number, units: UnitSystem): number {
  return units === "imperial" ? km / KM_PER_MILE : km;
}

export function displayDistanceToKm(value: number, units: UnitSystem): number {
  return units === "imperial" ? value * KM_PER_MILE : value;
}

/** Pace sec per km → sec per mile when imperial. */
export function paceSecPerKmToDisplay(secPerKm: number, units: UnitSystem): number {
  if (!secPerKm || !Number.isFinite(secPerKm)) return 0;
  return units === "imperial" ? secPerKm * KM_PER_MILE : secPerKm;
}

export function formatDistanceValue(
  km: number,
  units: UnitSystem,
  opts?: { digits?: number },
): string {
  const digits = opts?.digits ?? (km >= 10 ? 1 : 2);
  const v = kmToDisplayDistance(km, units);
  return v.toFixed(digits).replace(/\.0$/, "").replace(/(\.\d)0$/, "$1");
}

export function formatDistanceWithUnit(km: number, units: UnitSystem, opts?: { digits?: number }): string {
  const n = formatDistanceValue(km, units, opts);
  return units === "imperial" ? `${n} mi` : `${n} km`;
}

export function formatMetersLabel(meters: number, units: UnitSystem): string {
  if (units === "imperial") {
    const miles = meters / 1000 / KM_PER_MILE;
    if (miles < 0.1) {
      const ft = meters * 3.28084;
      return `${Math.round(ft)} ft`;
    }
    return `${miles.toFixed(miles >= 10 ? 1 : 2).replace(/\.0$/, "")} mi`;
  }
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(2).replace(/\.0$/, "")} km`;
}

export function distanceUnitShort(units: UnitSystem): string {
  return units === "imperial" ? "mi" : "km";
}

export function paceUnitLabel(units: UnitSystem): string {
  return units === "imperial" ? "/mi" : "/km";
}

export function formatPaceDisplay(secPerKm: number, units: UnitSystem): string {
  const sec = paceSecPerKmToDisplay(secPerKm, units);
  if (!sec || !Number.isFinite(sec) || sec <= 0 || sec > 3600) return "--:--";
  const total = Math.round(sec);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function kgToDisplayWeight(kg: number, units: UnitSystem): number {
  if (units !== "imperial") return kg;
  // Whole pounds so 170 lb ↔ 77 kg round-trips cleanly (DB stores integer kg).
  return Math.round(kg * LB_PER_KG);
}

export function displayWeightToKg(value: number, units: UnitSystem): number {
  return units === "imperial" ? value / LB_PER_KG : value;
}

export function formatWeightWithUnit(kg: number, units: UnitSystem, digits?: number): string {
  if (units === "imperial") {
    const lb = kgToDisplayWeight(kg, "imperial");
    return `${lb} lb`;
  }
  const d = digits ?? 1;
  const n = kg.toFixed(d).replace(/\.0$/, "");
  return `${n} kg`;
}

export function weightUnitLabel(units: UnitSystem): string {
  return units === "imperial" ? "lb" : "kg";
}

export function cmToDisplayHeight(cm: number, units: UnitSystem): { ft?: number; inch?: number; cm?: number } {
  if (units !== "imperial") return { cm };
  const totalIn = cm * IN_PER_CM;
  const ft = Math.floor(totalIn / 12);
  const inch = Math.round(totalIn - ft * 12);
  return { ft, inch: inch === 12 ? 0 : inch, ...(inch === 12 ? { ft: ft + 1 } : {}) };
}

export function displayHeightToCm(ft: number, inch: number): number {
  return Math.round((ft * 12 + inch) * 2.54);
}

/**
 * Parse user height input into cm.
 * Imperial accepts: 6, 6', 6'0, 6'0", 6 ft, 6ft 2in, 5 10, etc.
 * Metric accepts plain cm numbers.
 */
export function parseHeightToCm(raw: string, units: UnitSystem): number | null {
  const s = String(raw || "").trim();
  if (!s) return null;

  if (units === "imperial") {
    const ftIn = s.match(/^(\d+)\s*(?:'|ft|feet)?\s*[,\-]?\s*(\d{1,2})?\s*(?:"|in|inch|inches)?$/i);
    if (ftIn) {
      const ft = parseInt(ftIn[1], 10);
      const inch = ftIn[2] != null && ftIn[2] !== "" ? parseInt(ftIn[2], 10) : 0;
      if (!Number.isFinite(ft) || ft < 3 || ft > 8) return null;
      if (!Number.isFinite(inch) || inch < 0 || inch >= 12) return null;
      return displayHeightToCm(ft, inch);
    }
    // Bare number 3–8 → feet (e.g. "6" = 6'0")
    const bare = parseFloat(s);
    if (Number.isFinite(bare) && bare >= 3 && bare <= 8 && !s.includes(".")) {
      return displayHeightToCm(Math.floor(bare), 0);
    }
    return null;
  }

  const cm = parseFloat(s.replace(",", "."));
  if (!Number.isFinite(cm)) return null;
  return Math.round(cm);
}

/** Parse weight input → kg. Imperial = pounds. Always returns a finite number or null. */
export function parseWeightToKg(raw: string, units: UnitSystem): number | null {
  const n = parseFloat(String(raw || "").trim().replace(",", "."));
  if (!Number.isFinite(n) || n <= 0) return null;
  return displayWeightToStoredKg(n, units);
}

/** Profiles.height_cm is INTEGER — round for storage. Prefer displayWeightToStoredKg for weight. */
export function roundMetricForProfile(kgOrCm: number | null): number | null {
  if (kgOrCm == null || !Number.isFinite(kgOrCm)) return null;
  return Math.round(kgOrCm);
}

/** Convert display weight to kg for profiles.weight_kg (numeric, 2 decimals).
 * Imperial: store kg such that whole-lb display matches the entered pounds.
 */
export function displayWeightToStoredKg(value: number, units: UnitSystem): number | null {
  if (!Number.isFinite(value) || value <= 0) return null;
  if (units !== "imperial") return Math.round(value * 10) / 10;
  const targetLb = Math.round(value);
  return Math.round((targetLb / LB_PER_KG) * 100) / 100;
}

/** Run announcement distance presets (stored as stable string keys). */
export const AUDIO_DISTANCE_INTERVALS_METRIC = ["500m", "1km", "5km"] as const;
export const AUDIO_DISTANCE_INTERVALS_IMPERIAL = ["0.25mi", "0.5mi", "1mi"] as const;
export type AudioDistanceInterval =
  | (typeof AUDIO_DISTANCE_INTERVALS_METRIC)[number]
  | (typeof AUDIO_DISTANCE_INTERVALS_IMPERIAL)[number];

export const METERS_PER_MILE = 1609.344;

/** Auto-split length: 1 km or 1 mile (Strava-style). */
export function splitLengthMeters(units: UnitSystem): number {
  return units === "imperial" ? METERS_PER_MILE : 1000;
}

/** Short label for split index column ("km" / "mi"). */
export function splitUnitShort(units: UnitSystem): string {
  return units === "imperial" ? "mi" : "km";
}

/** Convert a split's wall-clock seconds into stored sec/km (mile splits normalize). */
export function splitSecondsToPaceSecPerKm(splitSeconds: number, units: UnitSystem): number {
  if (!Number.isFinite(splitSeconds) || splitSeconds <= 0) return 0;
  return units === "imperial" ? splitSeconds / (METERS_PER_MILE / 1000) : splitSeconds;
}

export function audioDistanceIntervalsForUnits(units: UnitSystem): readonly AudioDistanceInterval[] {
  return units === "imperial" ? AUDIO_DISTANCE_INTERVALS_IMPERIAL : AUDIO_DISTANCE_INTERVALS_METRIC;
}

export function audioDistanceIntervalMeters(key: string): number {
  switch (key) {
    case "500m":
      return 500;
    case "1km":
      return 1000;
    case "5km":
      return 5000;
    case "0.25mi":
      return Math.round(0.25 * METERS_PER_MILE);
    case "0.5mi":
      return Math.round(0.5 * METERS_PER_MILE);
    case "1mi":
      return Math.round(METERS_PER_MILE);
    default:
      return 0;
  }
}

export function isAudioDistanceInterval(key: string): boolean {
  return audioDistanceIntervalMeters(key) > 0;
}

/** Map a saved distance interval to the equivalent option for the current unit system. */
export function normalizeAudioDistanceInterval(
  key: string,
  units: UnitSystem,
): AudioDistanceInterval | string {
  if (units === "imperial") {
    if (key === "500m") return "0.25mi";
    if (key === "1km") return "0.5mi";
    if (key === "5km") return "1mi";
  } else {
    if (key === "0.25mi") return "500m";
    if (key === "0.5mi") return "1km";
    if (key === "1mi") return "5km";
  }
  return key;
}

export function formatAudioDistanceIntervalLabel(key: string): string {
  switch (key) {
    case "500m":
      return "500m";
    case "1km":
      return "1km";
    case "5km":
      return "5km";
    case "0.25mi":
      return "0.25mi";
    case "0.5mi":
      return "0.5mi";
    case "1mi":
      return "1mi";
    default:
      return key;
  }
}

export function formatHeightDisplay(cm: number, units: UnitSystem): string {
  if (units !== "imperial") return `${Math.round(cm)} cm`;
  const { ft = 0, inch = 0 } = cmToDisplayHeight(cm, "imperial");
  return `${ft}'${inch}"`;
}
